export type FinanceImportDirection = 'expense' | 'income' | 'transfer';

/** One row extracted by the LLM before resolution against master data. */
export type CanonicalImportRow = {
  date: string;
  amount: number;
  direction: FinanceImportDirection;
  /**
   * True when debit vs credit came from a sign, a DR/CR marker, a type column,
   * or separate debit/credit columns. An unsigned amount with no other signal
   * leaves this false so a known category kind can still decide direction.
   */
  directionExplicit?: boolean;
  currencyCode: string;
  categoryLabel: string | null;
  /** Category text from the file. The model maps it onto the user's categories. */
  sourceCategoryLabel?: string | null;
  accountHint: string | null;
  description: string | null;
};

export type LlmExtractedRow = {
  date?: unknown;
  amount?: unknown;
  direction?: unknown;
  currencyCode?: unknown;
  currency?: unknown;
  categoryLabel?: unknown;
  category?: unknown;
  accountHint?: unknown;
  account?: unknown;
  description?: unknown;
  notes?: unknown;
  memo?: unknown;
};

function isRealDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

function iso(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export type ImportDateOrder = 'mdy' | 'dmy';

type NumericDate = { a: number; b: number; year: number; sep: string };

const MONTH_INDEX: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

const MONTH_NAME =
  'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';

function numericDate(value: string): NumericDate | null {
  const v = value.trim().split(/[ T]/)[0] ?? '';
  const m = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{4})$/.exec(v);
  if (!m) return null;
  return { a: Number(m[1]), b: Number(m[3]), year: Number(m[4]), sep: m[2]! };
}

function dateWithOrder(value: NumericDate, order: ImportDateOrder): string | null {
  const month = order === 'mdy' ? value.a : value.b;
  const day = order === 'mdy' ? value.b : value.a;
  return isRealDate(value.year, month, day) ? iso(value.year, month, day) : null;
}

function numericDatesInText(text: string): NumericDate[] {
  const found: NumericDate[] = [];
  for (const match of text.matchAll(/\b(\d{1,2}[/.-]\d{1,2}[/.-]\d{4})\b/g)) {
    const parsed = numericDate(match[1] ?? '');
    if (parsed) found.push(parsed);
  }
  return found;
}

function monthEndIso(year: number, month: number): string {
  const d = new Date(Date.UTC(year, month, 0));
  return iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** Months named on the statement, widened to whole months so the period can anchor numeric dates. */
function statementMonthWindow(text: string): { start: string; end: string } | null {
  const dates: string[] = [];
  const dayFirst = new RegExp(`\\b(\\d{1,2})\\s+(${MONTH_NAME})\\s+(\\d{4})\\b`, 'gi');
  const monthFirst = new RegExp(`\\b(${MONTH_NAME})\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s+(\\d{4})\\b`, 'gi');
  for (const match of text.matchAll(dayFirst)) {
    const day = Number(match[1]);
    const month = MONTH_INDEX[match[2]!.toLowerCase()];
    const year = Number(match[3]);
    if (month && isRealDate(year, month, day)) dates.push(iso(year, month, day));
  }
  for (const match of text.matchAll(monthFirst)) {
    const month = MONTH_INDEX[match[1]!.toLowerCase()];
    const day = Number(match[2]);
    const year = Number(match[3]);
    if (month && isRealDate(year, month, day)) dates.push(iso(year, month, day));
  }
  for (const match of text.matchAll(/\b(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})\b/g)) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (isRealDate(year, month, day)) dates.push(iso(year, month, day));
  }
  if (dates.length === 0) return null;
  const startMonth = dates.reduce((min, date) => (date < min ? date : min)).slice(0, 7);
  const endMonth = dates.reduce((max, date) => (date > max ? date : max)).slice(0, 7);
  const [endYear, endMonthNum] = endMonth.split('-').map(Number);
  return { start: `${startMonth}-01`, end: monthEndIso(endYear!, endMonthNum!) };
}

/**
 * One day/month order for a whole statement.
 * A day above 12 locks it. Otherwise the statement period picks the reading
 * that lands inside that period. Null keeps the per-value separator default.
 */
export function inferImportDateOrder(samples: string[], contextText = ''): ImportDateOrder | null {
  const dates = [
    ...samples.flatMap((sample) => {
      const parsed = numericDate(sample);
      return parsed ? [parsed] : [];
    }),
    ...numericDatesInText(contextText),
  ];
  let sawDayFirst = false;
  let sawMonthFirst = false;
  for (const date of dates) {
    if (date.a > 12 && date.b <= 12) sawDayFirst = true;
    else if (date.b > 12 && date.a <= 12) sawMonthFirst = true;
  }
  if (sawDayFirst && !sawMonthFirst) return 'dmy';
  if (sawMonthFirst && !sawDayFirst) return 'mdy';
  if (sawDayFirst && sawMonthFirst) return null;

  const window = statementMonthWindow(contextText);
  if (!window || dates.length === 0) return null;
  const fit = (order: ImportDateOrder) =>
    dates.filter((date) => {
      const isoDate = dateWithOrder(date, order);
      return isoDate != null && isoDate >= window.start && isoDate <= window.end;
    }).length;
  const monthFirst = fit('mdy');
  const dayFirst = fit('dmy');
  if (monthFirst === dayFirst) return null;
  return monthFirst > dayFirst ? 'mdy' : 'dmy';
}

function swapDayAndMonth(isoDate: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month > 12 || day > 12 || month === day) return null;
  if (!isRealDate(year, day, month)) return null;
  return iso(year, day, month);
}

/**
 * When the model swapped day and month, put the date back if the statement
 * text only agrees with the other reading.
 */
export function alignImportDatesToStatement<T extends { date: string }>(
  rows: T[],
  sourceText: string,
): T[] {
  const order = inferImportDateOrder([], sourceText);
  if (!order) return rows;
  const expected = new Set(
    numericDatesInText(sourceText).flatMap((date) => {
      const isoDate = dateWithOrder(date, order);
      return isoDate ? [isoDate] : [];
    }),
  );
  if (expected.size === 0) return rows;
  return rows.map((row) => {
    const swapped = swapDayAndMonth(row.date);
    if (!swapped) return row;
    if (expected.has(swapped) && !expected.has(row.date)) return { ...row, date: swapped };
    return row;
  });
}

/** Parse a date cell to `YYYY-MM-DD`, or null if unparseable. */
export function parseImportDate(value: string, order?: ImportDateOrder | null): string | null {
  const v = value.trim().split(/[ T]/)[0] ?? '';
  if (v === '') return null;

  const m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(v);
  if (m) {
    const [, y, mo, d] = m.map(Number);
    return isRealDate(y!, mo!, d!) ? iso(y!, mo!, d!) : null;
  }

  const numeric = numericDate(v);
  if (!numeric) return null;
  if (order === 'mdy' || order === 'dmy') return dateWithOrder(numeric, order);
  const { a, b, year, sep } = numeric;
  let day: number;
  let month: number;
  if (a > 12 && b <= 12) {
    day = a;
    month = b;
  } else if (b > 12 && a <= 12) {
    month = a;
    day = b;
  } else if (sep === '/') {
    month = a;
    day = b;
  } else {
    day = a;
    month = b;
  }
  return isRealDate(year, month, day) ? iso(year, month, day) : null;
}

const UNICODE_DASHES = /[\u2212\u2012\u2013\u2014\uFE63\uFF0D]/g;

/** Parse an amount to an unsigned number, or null if invalid. */
export function parseImportAmount(value: unknown): number | null {
  if (typeof value === 'number') {
    const n = Math.abs(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (typeof value !== 'string') return null;
  const cleaned = value
    .replace(UNICODE_DASHES, '-')
    .replace(/[^0-9.,-]/g, '')
    .replace(/,/g, '')
    .replace(/-$/, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = Math.abs(Number(cleaned));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Amount plus an explicit debit/credit signal from the cell itself.
 * `direction` is null when the cell is an unsigned number.
 */
export function parseSignedImportAmount(value: string): {
  amount: number | null;
  direction: 'expense' | 'income' | null;
} {
  const text = value.trim().replace(UNICODE_DASHES, '-');
  if (!text) return { amount: null, direction: null };

  let direction: 'expense' | 'income' | null = null;
  if (/(^|[^a-z])cr([^a-z]|$)/i.test(text)) direction = 'income';
  else if (/(^|[^a-z])dr([^a-z]|$)/i.test(text)) direction = 'expense';
  else if (text.includes('(') && text.includes(')')) direction = 'expense';
  else {
    const withoutWords = text.replace(/[a-zA-Z]/g, '');
    if (withoutWords.includes('-')) direction = 'expense';
    else if (withoutWords.includes('+')) direction = 'income';
  }

  const numeric = text
    .replace(/[()]/g, '')
    .replace(/(^|[^a-z])cr([^a-z]|$)/gi, ' ')
    .replace(/(^|[^a-z])dr([^a-z]|$)/gi, ' ')
    .replace(/[-+]/g, '');
  return { amount: parseImportAmount(numeric), direction };
}

const EXPENSE_DIRECTION_TOKENS = new Set([
  'expense',
  'debit',
  'debits',
  'out',
  'outflow',
  'outflows',
  'withdrawal',
  'withdrawals',
  'charge',
  'charges',
  'purchase',
  'sale',
  'dr',
  'db',
  'moneyout',
  'spent',
  'paidout',
  'outgoing',
  'outbound',
]);

const INCOME_DIRECTION_TOKENS = new Set([
  'income',
  'credit',
  'credits',
  'in',
  'inflow',
  'inflows',
  'deposit',
  'deposits',
  'refund',
  'refunds',
  'cr',
  'moneyin',
  'paidin',
  'incoming',
  'inbound',
]);

export function normalizeDirection(value: unknown): FinanceImportDirection | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!v) return null;
  if (EXPENSE_DIRECTION_TOKENS.has(v)) return 'expense';
  if (INCOME_DIRECTION_TOKENS.has(v)) return 'income';
  if (v === 'transfer' || v === 'neutral') return 'transfer';
  return null;
}

export function normalizeCurrencyCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

/** Strip markdown code fences and trailing commentary from LLM output. */
export function stripLlmJsonWrapper(text: string): string {
  let trimmed = text.trim();
  if (trimmed.startsWith('```')) {
    trimmed = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
  }
  const firstBrace = trimmed.indexOf('[');
  const lastBrace = trimmed.lastIndexOf(']');
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    return trimmed.slice(firstBrace, lastBrace + 1);
  }
  return trimmed;
}

export function parseLlmRow(raw: LlmExtractedRow, index: number): { row: CanonicalImportRow | null; error: string | null } {
  const dateRaw = raw.date;
  const date =
    typeof dateRaw === 'string'
      ? parseImportDate(dateRaw)
      : dateRaw != null
        ? parseImportDate(String(dateRaw))
        : null;
  if (!date) {
    return { row: null, error: `Row ${index}: invalid or missing date` };
  }

  const amount = parseImportAmount(raw.amount);
  if (amount == null) {
    return { row: null, error: `Row ${index}: invalid or missing amount` };
  }

  const parsedDirection = normalizeDirection(raw.direction);
  const direction = parsedDirection ?? 'expense';
  const currencyRaw = raw.currencyCode ?? raw.currency;
  const currencyCode = normalizeCurrencyCode(currencyRaw ?? '');
  if (!currencyCode) {
    return { row: null, error: `Row ${index}: invalid or missing currency` };
  }

  const categoryRaw = raw.categoryLabel ?? raw.category;
  const categoryLabel =
    typeof categoryRaw === 'string' && categoryRaw.trim() !== '' ? categoryRaw.trim() : null;

  const accountRaw = raw.accountHint ?? raw.account;
  const accountHint =
    typeof accountRaw === 'string' && accountRaw.trim() !== '' ? accountRaw.trim() : null;

  const descRaw = raw.description ?? raw.notes ?? raw.memo;
  const description =
    typeof descRaw === 'string' && descRaw.trim() !== '' ? descRaw.trim() : null;

  return {
    row: {
      date,
      amount,
      direction,
      directionExplicit: parsedDirection != null,
      currencyCode,
      categoryLabel,
      accountHint,
      description,
    },
    error: null,
  };
}

export function parseLlmRowsJson(text: string): {
  rows: CanonicalImportRow[];
  errors: string[];
} {
  const cleaned = stripLlmJsonWrapper(text);
  let parsed: unknown;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    return { rows: [], errors: ['LLM output was not valid JSON'] };
  }

  if (!Array.isArray(parsed)) {
    return { rows: [], errors: ['LLM output must be a JSON array'] };
  }

  const rows: CanonicalImportRow[] = [];
  const errors: string[] = [];
  parsed.forEach((item, index) => {
    const result = parseLlmRow((item ?? {}) as LlmExtractedRow, index);
    if (result.row) rows.push(result.row);
    else if (result.error) errors.push(result.error);
  });
  return { rows, errors };
}

function lineHasDate(line: string): boolean {
  const pattern = /\b(?:\d{4}[-/.]\d{1,2}[-/.]\d{1,2}|\d{1,2}[-/.]\d{1,2}[-/.]\d{4})\b/g;
  for (const match of line.matchAll(pattern)) {
    const token = match[0].replace(/\./g, '-');
    if (parseImportDate(token)) return true;
  }
  return false;
}

/**
 * Split statement text for LLM calls.
 * Every line is sent. A dated line and the undated lines under it stay in the
 * same chunk, so a date is not separated from its amount or memo.
 * A short undated preamble (account name, currency) is repeated on later chunks.
 */
export function chunkImportText(text: string, maxChars = 12000): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  if (trimmed.length <= maxChars) return [trimmed];

  const lines = trimmed.split(/\r?\n/);
  const blocks: string[][] = [];
  let block: string[] = [];
  for (const line of lines) {
    if (block.length > 0 && lineHasDate(line)) {
      blocks.push(block);
      block = [line];
    } else {
      block.push(line);
    }
  }
  if (block.length > 0) blocks.push(block);

  const preamble = blocks[0]?.some((line) => lineHasDate(line)) ? null : (blocks[0] ?? null);
  const transactions = preamble ? blocks.slice(1) : blocks;
  if (transactions.length === 0) return [trimmed];

  const preambleText = preamble?.join('\n') ?? '';
  const repeatPreamble = preambleText.length > 0 && preambleText.length < maxChars;
  const chunks: string[] = [];
  let group: string[] = [];
  let groupChars = 0;

  const pushGroup = () => {
    if (group.length === 0) return;
    const body = group.join('\n');
    const withPreamble =
      chunks.length === 0 || repeatPreamble
        ? preambleText
          ? `${preambleText}\n${body}`
          : body
        : body;
    chunks.push(withPreamble);
    group = [];
    groupChars = 0;
  };

  for (const transaction of transactions) {
    const transactionText = transaction.join('\n');
    const prefixChars =
      chunks.length === 0 || repeatPreamble ? preambleText.length + (preambleText ? 1 : 0) : 0;
    const nextChars = groupChars + (group.length > 0 ? 1 : 0) + transactionText.length;
    if (group.length > 0 && prefixChars + nextChars > maxChars) pushGroup();
    groupChars += (group.length > 0 ? 1 : 0) + transactionText.length;
    group.push(transactionText);
  }
  pushGroup();
  return chunks.filter((chunk) => chunk.trim() !== '');
}

/**
 * Close a truncated JSON array at the last complete object.
 * A streaming extract often dies mid-object; the earlier objects are still usable.
 */
export function salvagePartialLlmArray(text: string): string | null {
  const start = text.indexOf('[');
  if (start < 0) return null;
  const body = text.slice(start);
  let depth = 0;
  let inString = false;
  let escape = false;
  let lastObjectEnd = -1;

  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i]!;
    if (inString) {
      if (escape) escape = false;
      else if (ch === '\\') escape = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{' || ch === '[') depth += 1;
    else if (ch === '}' || ch === ']') {
      depth -= 1;
      if (ch === '}' && depth === 1) lastObjectEnd = i;
    }
  }

  if (lastObjectEnd < 0) return null;
  return `${body.slice(0, lastObjectEnd + 1)}]`;
}

export type ExtractWaitStage = 'connecting' | 'headers' | 'streaming';

export function extractWaitDetail(input: {
  call: number;
  calls: number;
  attempt: number;
  attempts: number;
  stage: ExtractWaitStage;
  elapsedSec: number;
  idleLimitSec: number;
  quietSec: number;
  receivedChars: number;
  promptChars: number;
  chunkChars: number;
  model: string;
  bodyBytes?: number;
  sseEvents?: number;
  reasoningChars?: number;
  generationId?: string | null;
}): string {
  const where = `Extract ${input.call}/${input.calls}, attempt ${input.attempt}/${input.attempts}`;
  const bodyBytes = input.bodyBytes ?? 0;
  const sseEvents = input.sseEvents ?? 0;
  const reasoningChars = input.reasoningChars ?? 0;
  const generation = input.generationId ? `generation ${input.generationId}` : 'no generation id';
  if (input.stage === 'connecting') {
    return `${where}: request sent, no HTTP response yet (${input.elapsedSec}s / ${input.idleLimitSec}s). Prompt ${input.promptChars} chars, statement chunk ${input.chunkChars} chars, model ${input.model}.`;
  }
  if (input.stage === 'headers' && bodyBytes === 0) {
    return `${where}: HTTP 200, response body still empty (${input.quietSec}s / ${input.idleLimitSec}s). 0 bytes, 0 SSE events. Prompt ${input.promptChars} chars, statement chunk ${input.chunkChars} chars, model ${input.model}.`;
  }
  if (input.stage === 'headers') {
    return `${where}: HTTP 200, ${bodyBytes} body bytes, ${sseEvents} SSE events, ${reasoningChars} reasoning chars, 0 transaction chars, ${generation} (${input.quietSec}s / ${input.idleLimitSec}s since last byte). Model ${input.model}.`;
  }
  return `${where}: transaction text arriving, ${input.receivedChars} chars, ${bodyBytes} body bytes, ${sseEvents} SSE events, ${reasoningChars} reasoning chars, ${generation}, last byte ${input.quietSec}s ago. Model ${input.model}.`;
}

export function extractStopDetail(input: {
  call: number;
  calls: number;
  reason: 'no-response' | 'no-tokens' | 'stalled' | 'wall';
  elapsedSec: number;
  idleLimitSec: number;
  receivedChars: number;
  keptRows: number;
  promptChars: number;
  chunkChars: number;
  model: string;
  bodyBytes?: number;
  sseEvents?: number;
  reasoningChars?: number;
  generationId?: string | null;
}): string {
  const where = `Extract ${input.call}/${input.calls}`;
  const bodyBytes = input.bodyBytes ?? 0;
  const sseEvents = input.sseEvents ?? 0;
  const reasoningChars = input.reasoningChars ?? 0;
  const generation = input.generationId ? `generation ${input.generationId}` : 'no generation id';
  const kept =
    input.keptRows > 0
      ? ` Kept ${input.keptRows} complete transactions from the partial output.`
      : '';
  if (input.reason === 'no-response') {
    return `${where} stopped after ${input.elapsedSec}s: no HTTP response (limit ${input.idleLimitSec}s). Prompt ${input.promptChars} chars, statement chunk ${input.chunkChars} chars, model ${input.model}. This server aborted the request; OpenRouter had not sent a status line.`;
  }
  if (input.reason === 'no-tokens' && bodyBytes === 0) {
    return `${where} stopped after ${input.elapsedSec}s: HTTP 200, then the response body stayed empty for ${input.idleLimitSec}s (0 bytes, 0 SSE events). Prompt ${input.promptChars} chars, statement chunk ${input.chunkChars} chars, model ${input.model}. This server aborted the request. OpenRouter accepted it and sent no body.`;
  }
  if (input.reason === 'no-tokens') {
    return `${where} stopped after ${input.elapsedSec}s: HTTP 200, ${bodyBytes} body bytes, ${sseEvents} SSE events, ${reasoningChars} reasoning chars, ${input.receivedChars} transaction chars, ${generation}. No new body bytes for ${input.idleLimitSec}s. Model ${input.model}.`;
  }
  if (input.reason === 'wall') {
    return `${where} hit the ${input.elapsedSec}s overall cap with ${input.receivedChars} transaction chars, ${bodyBytes} body bytes, ${sseEvents} SSE events, ${reasoningChars} reasoning chars, ${generation}. Model ${input.model}.${kept}`;
  }
  return `${where} stopped after ${input.elapsedSec}s: no new body bytes for ${input.idleLimitSec}s at ${input.receivedChars} transaction chars, ${bodyBytes} body bytes, ${sseEvents} SSE events, ${reasoningChars} reasoning chars, ${generation}. Model ${input.model}.${kept}`;
}

/** Keep rows the user already edited when a later parse snapshot arrives. */
export function mergePreservingEdits<T extends { rowIndex: number }>(
  local: readonly T[],
  incoming: readonly T[],
  dirty: ReadonlySet<number>,
): T[] {
  const localByIndex = new Map(local.map((row) => [row.rowIndex, row]));
  return incoming.map((row) => {
    const edited = localByIndex.get(row.rowIndex);
    if (edited && dirty.has(row.rowIndex)) return edited;
    return row;
  });
}
